<!-- 
Dialog for resending a notice.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-form ref="form">
      <v-card flat>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>
            {{ title }}
            <span v-if="subtitle" class="body-1">{{ subtitle }}</span>
          </v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-notices" />
        </v-toolbar>
        <our-busy-indicator :show="resending"/>
        <our-loading-indicator v-if="loading"/>
        <v-card-text v-else>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs10 sm10 md6 offset-xs1>
                <v-text-field
                  v-if="show" :value="to" :rules="[rules.required, rules.emails]" clearable
                  label="To*" hint="Separate multiple email addresses with a comma" required
                  @input="update('to', $event)"
                />
              </v-flex>
              <v-flex 
                xs10 sm10 md5 
                offset-xs1 offset-md0
              >
                <v-text-field
                  v-if="show" :value="cc" :rules="[rules.emails]" clearable
                  label="Cc" hint="Separate multiple email addresses with a comma" 
                  @input="update('cc', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <notice-email-panel :notice="notice" hide-recipients/>
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="resending || loading" flat @click="hide">Cancel</v-btn>
          <v-btn :disabled="resending || loading" flat color="success" @click="resendNotice" >Resend {{ typeDescription }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form"/>
  </v-dialog>
</template>

<script>
import NoticeEmailPanel from "@/components/notices/NoticeEmailPanel.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import * as action from "@/store/notices/action-types";
import * as mutation from "@/store/notices/mutation-types";

export default {
  components: {
    NoticeEmailPanel
  },
  mixins: [lookupMixins, ruleMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    },
    providerId: {
      type: Number,
      default: null
    },
    templateType: {
      type: String,
      default: null
    },
    noticeId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      activeTab: 0,
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.notices.resend;
    },
    title() {
      return this.loaded ? `Resend: ${this.typeDescription}` : "Resend notice";
    },
    subtitle() {
      return this.loaded ? `${this.levyCollection} - ${this.providerName}` : null;
    },
    preview() {
      return this.$store.state.notices.previewResend;
    },
    loading() {
      return this.preview.loading;
    },
    loaded() {
      return !this.loading && this.notice;
    },
    resending() {
      return this.state.loading;
    },
    notice() {
      return this.$store.state.notices.previewResend.data;
    },
    to() {
      return this.state.data.to;
    },
    cc() {
      return this.state.data.cc;
    },
    typeDescription() {
      return this.loaded ? this.lookups.noticeTypeDescriptions[this.notice.type] : null;
    },
    levyCollection() {
      return this.loaded ? this.notice.levyCollection : null;
    },
    providerName() {
      return this.loaded ? this.notice.providerName : null;
    }
  },
  watch: {
    show(showing) {
      if (showing) {
        this.load();
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.RESEND_SET_DATA, { [field]: newValue });
    },
    async load() {
      this.$store.commit(mutation.RESEND_SET_DATA, { noticeId: this.noticeId });
      await this.$store.dispatch(action.PREVIEW_RESEND, this.noticeId);
      this.update("to", this.notice.to);
      this.update("cc", this.notice.cc);
    },
    async resendNotice() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.RESEND, this.noticeId))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
